import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface FilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    // We can add onApply later to pass the state back to the parent
    onApply?: (filters: any) => void;
}

export function FilterModal({ isOpen, onClose, onApply }: FilterModalProps) {
    const { t } = useTranslation(['common', 'offers', 'profile', 'staking']);
    const { selectionChanged, impactOccurred } = useHapticFeedback();
    
    // State
    const [priceRange, setPriceRange] = useState<number>(3000); // represents the slider value (max)
    const [minPrice, setMinPrice] = useState<number>(100);
    const [maxPrice, setMaxPrice] = useState<number>(5000);
    
    const [category, setCategory] = useState<'store' | 'auctions'>('store');
    const [selectedTags, setSelectedTags] = useState<string[]>(['Popular']);
    const [sortBy, setSortBy] = useState<string>('Newest');

    const tags = ['Featured', 'Premium', 'Popular', 'Live', 'Fast', 'Active', 'Ending'];
    const sortOptions = ['Newest', 'Price: Low to High', 'Price: High to Low', 'Popularity'];

    // Sync slider with max price visually, or handle dual nature
    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(e.target.value);
        setPriceRange(val);
        setMaxPrice(val);
        if (minPrice > val) setMinPrice(val);
        selectionChanged();
    };

    const toggleTag = (tag: string) => {
        selectionChanged();
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    const handleClearAll = () => {
        impactOccurred('medium');
        setPriceRange(5000);
        setMinPrice(100);
        setMaxPrice(5000);
        setCategory('store');
        setSelectedTags([]);
        setSortBy('Newest');
    };

    const handleApply = () => {
        impactOccurred('heavy');
        if (onApply) {
            onApply({
                minPrice,
                maxPrice,
                category,
                tags: selectedTags,
                sortBy
            });
        }
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Filter">
            <div className="space-y-8 pb-4">
                
                {/* 1. Price Range */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-[15px] font-semibold text-white">Price Range <span className="text-white/40 font-normal">(GRAM)</span></h3>
                    </div>
                    
                    {/* Linear Slider (Meqyas Kheyte) */}
                    <div className="relative mb-6 pt-2">
                        <div className="flex justify-between text-xs text-white/50 mb-3 font-medium px-1">
                            <span>100</span>
                            <span>5,000</span>
                        </div>
                        <input
                            type="range"
                            min="100"
                            max="5000"
                            step="10"
                            value={priceRange}
                            onChange={handleSliderChange}
                            className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer outline-none"
                            style={{
                                background: `linear-gradient(to right, #B4F73D 0%, #B4F73D ${(priceRange - 100) / (5000 - 100) * 100}%, rgba(255,255,255,0.1) ${(priceRange - 100) / (5000 - 100) * 100}%, rgba(255,255,255,0.1) 100%)`
                            }}
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex-1 glass-inset rounded-2xl p-3 relative group transition-colors">
                            <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold block mb-1">Min</label>
                            <input
                                type="number"
                                value={minPrice}
                                onChange={(e) => setMinPrice(Number(e.target.value))}
                                className="w-full bg-transparent text-white text-lg font-bold focus:outline-none"
                            />
                        </div>
                        <div className="flex-1 glass-inset rounded-2xl p-3 relative group transition-colors">
                            <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold block mb-1">MAX</label>
                            <input
                                type="number"
                                value={maxPrice}
                                onChange={(e) => {
                                    setMaxPrice(Number(e.target.value));
                                    setPriceRange(Number(e.target.value));
                                }}
                                className="w-full bg-transparent text-white text-lg font-bold focus:outline-none"
                            />
                        </div>
                    </div>
                </div>

                {/* 2. Categories Segmented Control */}
                <div>
                    <h3 className="text-[15px] font-semibold text-white mb-3">Categories</h3>
                    <div className="flex p-1 bg-[#1c1c1e] rounded-2xl border border-white/5">
                        <button
                            onClick={() => { selectionChanged(); setCategory('store'); }}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${category === 'store' ? 'bg-[#2c2c2e] text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`}
                        >
                            Store
                        </button>
                        <button
                            onClick={() => { selectionChanged(); setCategory('auctions'); }}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${category === 'auctions' ? 'bg-[#2c2c2e] text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`}
                        >
                            Auctions
                        </button>
                    </div>
                </div>

                {/* 3. Tags / Filters */}
                <div>
                    <h3 className="text-[15px] font-semibold text-white mb-3">Filter</h3>
                    <div className="flex flex-wrap gap-2.5">
                        {tags.map((tag) => {
                            const isSelected = selectedTags.includes(tag);
                            return (
                                <button
                                    key={tag}
                                    onClick={() => toggleTag(tag)}
                                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${isSelected 
                                        ? 'bg-[#B4F73D]/10 text-[#B4F73D] border-[#B4F73D]/30' 
                                        : 'bg-[#1c1c1e] text-white/70 border-white/5 hover:bg-[#2c2c2e]'
                                    }`}
                                >
                                    {tag}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* 4. Sort By */}
                <div>
                    <h3 className="text-[15px] font-semibold text-white mb-3">Sort By</h3>
                    <div className="grid grid-cols-2 gap-2.5">
                        {sortOptions.map((sort) => {
                             const isSelected = sortBy === sort;
                             return (
                                <button 
                                    key={sort} 
                                    onClick={() => { selectionChanged(); setSortBy(sort); }} 
                                    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${isSelected 
                                        ? 'bg-[#2c2c2e] text-white border-white/10' 
                                        : 'bg-[#1c1c1e] text-white/60 border-white/5 hover:bg-[#2c2c2e]'
                                    }`}
                                >
                                    {sort}
                                </button>
                             );
                        })}
                    </div>
                </div>

                {/* Actions */}
                <div className="pt-2 flex gap-3">
                    <button 
                        onClick={handleClearAll} 
                        className="flex-1 py-4 bg-[#1c1c1e] border border-white/5 hover:bg-[#2c2c2e] text-white font-semibold rounded-2xl transition-colors active:scale-95"
                    >
                        Clear All
                    </button>
                    <button 
                        onClick={handleApply} 
                        className="flex-[2] py-4 bg-[#B4F73D] text-black font-bold rounded-2xl shadow-[0_4px_15px_rgba(180,247,61,0.2)] hover:shadow-[0_4px_20px_rgba(180,247,61,0.4)] transition-all active:scale-95"
                    >
                        Apply Filters
                    </button>
                </div>
                
            </div>
        </Modal>
    );
}
